import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounce, map, switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import {
  CuerpoTramosClp,
  CuerpoTramosUsdt,
  EditorTramosClpUsdtResponse,
  EditorTramosUsdtClpResponse,
  EditorTramosUsdtResponse,
  EditorTramosUsdtVesResponse,
  HistorialTramosUsdtPagina,
  HistorialTramoUsdt,
  MainRatesService,
  ModoTramoUsdt,
  PrevisualizarTramosClpUsdtResponse,
  PrevisualizarTramosUsdtClpResponse,
  PrevisualizarTramosUsdtResponse,
  PrevisualizarTramosUsdtVesResponse,
  TasaTramoUsdt,
} from '../../services/main-rates.service';
import {
  erroresDe,
  formatoNumero,
  formatoPct,
  mensajeConflicto,
  mensajeSinPermiso,
  mostrarPaginacion,
  paginaValida,
  statusDe,
  TEXTO_SOLO_LECTURA,
  totalPaginas,
  versionDe,
} from './franjas-editor.rules';
import * as T from './tramos-usdt-editor.rules';
import * as V from './tramos-usdt-ves-editor.rules';
import * as C from './tramos-clp-usdt-editor.rules';
import * as D from './tramos-distancia.rules';
import { EstadoRutaDistancia, TramosDistanciaService } from '../../services/tramos-distancia.service';

/**
 * Par que edita la pestaña. 'VES-USDT' (compra de USDT, la de siempre) o
 * 'USDT-VES' (venta de USDT, el espejo: techo en vez de piso y una tasa más
 * alta es mejor para el cliente). Desde el 22/10/2026 también 'CLP-USDT'
 * (compra) y 'USDT-CLP' (venta), con referencia en el spot USD/CLP y tope %.
 */
export type ParTramosUsdt = 'VES-USDT' | 'USDT-VES' | 'CLP-USDT' | 'USDT-CLP';

/**
 * Lo leído en el modal «Tasas por monto» se reusa durante este tiempo al
 * cambiar de pestaña (ir y volver no vuelve a pedir lo mismo). Recargar tras
 * un 409 y la relectura después de guardar siempre piden de nuevo.
 */
export const REUSO_PESTANA_MS = 30_000;

/** Editor leído por pestaña en este modal (lo guarda el modal, que vive más que la pestaña). */
export type LeidosTramosUsdt = Map<ParTramosUsdt, { en: number; r: unknown }>;

/** Lo que se avisa al guardar con éxito (misma forma que el de franjas). */
export interface TramosUsdtGuardados {
  par: ParTramosUsdt;
  cambios: number;
  aplicado: boolean;
}

type EditorTramos =
  | EditorTramosUsdtResponse
  | EditorTramosUsdtVesResponse
  | EditorTramosClpUsdtResponse
  | EditorTramosUsdtClpResponse;
type VistaTramos =
  | PrevisualizarTramosUsdtResponse
  | PrevisualizarTramosUsdtVesResponse
  | PrevisualizarTramosClpUsdtResponse
  | PrevisualizarTramosUsdtClpResponse;

type ResultadoVista =
  | { ok: true; r: VistaTramos }
  | { ok: false; errores: string[] };

/**
 * Pestaña «VES → USDT» del modal de franjas: margen mínimo (candado) y tramos
 * por monto en USDT. La comisión de la tasa base se muestra pero NO se edita
 * acá: mueve la base que también usan Vita VES→COP y VES→PEN, y se cambia en
 * su modal de siempre («Cambiar comisión» lo pide al modal padre).
 *
 * Mismo esquema que el editor CLP↔VES: el «Desde» del primero es editable y
 * los demás se encadenan con el «Hasta» anterior; todo se recalcula en el
 * navegador al instante y la vista previa oficial llega con debounce. La
 * cabecera (pestañas y cerrar) es del modal; esta pestaña pone cuerpo y pie.
 */
@Component({
  selector: 'app-tramos-usdt-editor',
  templateUrl: './tramos-usdt-editor.component.html',
  // Las clases del editor de franjas, para que se vea idéntico.
  styleUrls: ['./franjas-editor.component.sass', './tramos-usdt-editor.component.sass'],
})
export class TramosUsdtEditorComponent implements OnInit, OnDestroy {
  @Input() puedeEditar = false;

  /** Par de la pestaña. Por defecto VES→USDT, como siempre. */
  @Input() par: ParTramosUsdt = 'VES-USDT';

  /**
   * Lo que cada pestaña leyó en este modal: al volver a una se reusa (si es
   * de hace menos de `REUSO_PESTANA_MS`) en vez de pedir el editor otra vez.
   * Sin él (fuera del modal) se pide siempre, como antes.
   */
  @Input() leidos?: LeidosTramosUsdt;

  /** Se emite al guardar con éxito, para recargar el bloque del panel. */
  @Output() guardado = new EventEmitter<TramosUsdtGuardados>();

  /** Cancelar: el modal decide (confirma si hay cambios). */
  @Output() cerrarPedido = new EventEmitter<void>();

  /** «Cambiar comisión»: el modal confirma si hay cambios y abre el de comisión. */
  @Output() comisionPedida = new EventEmitter<void>();

  /** Cada edición: el modal borra la línea «Guardado → Aplicado» anterior. */
  @Output() editado = new EventEmitter<void>();

  static readonly DEBOUNCE_MS = 700;

  readonly motivoMax = T.MOTIVO_MAX;
  /** Tope del Manual %: 5 con el modelo de distancia (mejora sobre la chica, como el API), 50 con el de siempre. */
  get ajusteMax(): number {
    return this.esDistancia ? D.AJUSTE_DISTANCIA_MAX : T.AJUSTE_MAX;
  }
  readonly usdtEjemplo = T.USDT_EJEMPLO;
  readonly maxActivos = T.MAX_ACTIVOS;
  readonly textoSoloLectura = TEXTO_SOLO_LECTURA;
  readonly topeMax = C.TOPE_MAX_PCT;

  cargando = false;
  errorCarga: string | null = null;
  editor: EditorTramos | null = null;

  private inicial: T.BorradorTramos | null = null;
  /**
   * Versión de lo cargado (si el back la manda). Viaja en el PUT: si otro
   * operador guardó antes, el back responde 409 y se ofrece recargar.
   */
  private version: string | null = null;
  borrador: T.BorradorTramos | null = null;

  /**
   * Tope % (solo CLP↔USDT). Vive fuera del borrador para que el de VES quede
   * idéntico; viaja en el cuerpo como `topePct`.
   */
  tope: number | null = null;
  private topeInicial: number | null = null;

  /** Recalculado en cada cambio. */
  base: number | null = null;
  locales: T.TasaLocal[] = [];
  segmentos: T.SegmentoTramo[] = [];
  cambiosLista: string[] = [];

  simMonto: number | null = T.MONTO_SIM_VES;
  errorAccion: string | null = null;

  motivo = '';
  previsualizando = false;
  guardando = false;
  vista: VistaTramos | null = null;
  vistaVieja = false;
  /** Vista previa en camino (debounce o respuesta): no se guarda hasta que llegue. */
  vistaPendiente = false;
  errores: string[] = [];
  advertencias: string[] = [];
  mensaje: string | null = null;

  // Historial: plegado por defecto, 5 por página.
  historialAbierto = false;
  historialPagina = 1;
  historialItems: HistorialTramoUsdt[] = [];
  historialTotal = 0;
  cargandoHistorial = false;
  errorHistorial: string | null = null;
  private subHistorial?: Subscription;

  private cambios$ = new Subject<{ inmediato: boolean }>();
  private subVista: Subscription;

  /**
   * Estado de la ruta (`GET rates/tramos-usdt/{par}`) con los campos del
   * modelo de distancia (23/10/2026). Si el API dice `modelo = 'DISTANCIA'`,
   * las tasas estimadas siguen esa fórmula (chica → grande) y la ganancia se
   * mide contra su referencia. Sin él (API vieja), todo como antes.
   */
  estadoRuta: EstadoRutaDistancia | null = null;

  constructor(
    private mainRates: MainRatesService,
    // Opcional: los specs viejos montan el editor sin este servicio.
    @Optional() private distanciaSvc?: TramosDistanciaService,
  ) {
    this.subVista = this.cambios$
      .pipe(
        // `of(c).pipe(debounceTime)` emitía al completar (en cada tecla): ahora
        // espera DEBOUNCE_MS sin cambios; «inmediato» (cargar, Actualizar) va ya.
        debounce((c) => (c.inmediato ? of(0) : timer(TramosUsdtEditorComponent.DEBOUNCE_MS))),
        switchMap(() => this.pedirVista()),
      )
      .subscribe((res) => this.aplicarVista(res));
  }

  ngOnInit(): void {
    // USDT→VES / USDT→CLP: el cliente escribe los USDT que vende.
    if (this.esVenta) this.simMonto = V.MONTO_SIM_USDT;
    else if (this.esClp) this.simMonto = C.MONTO_SIM_CLP;
    this.cargar(false, true);
    this.leerEstadoRuta();
  }

  /** Lee el modelo de la ruta (nunca lanza: null = como siempre). */
  private leerEstadoRuta(): void {
    this.distanciaSvc?.obtenerEstadoRuta(this.par).subscribe((e) => {
      this.estadoRuta = e;
      if (this.esDistancia) this.recalcular();
    });
  }

  /** El API cobra esta ruta con el modelo de distancia. */
  get esDistancia(): boolean {
    return D.esModeloDistancia(this.estadoRuta);
  }

  /** PISO/TECHO de Oscar y la vista de la ruta (solo con el modelo de distancia). */
  get vistaDistancia(): D.VistaDistancia | null {
    return D.vistaDistancia(this.par, this.estadoRuta, this.comisionPct);
  }

  /** «0,9 – ∞» de la fila activa de la tabla de distancia. */
  get filaDistanciaTexto(): string | null {
    const f = this.estadoRuta?.filaDistancia;
    return f ? D.rangoFila(D.normalizarFila(f)) : null;
  }

  /** USDT→VES / USDT→CLP: el espejo (techo, tasa más alta mejor para el cliente). */
  get esVenta(): boolean {
    return String(this.par).startsWith('USDT-');
  }

  /** CLP→USDT / USDT→CLP (22/10/2026): spot USD/CLP, descuento base y tope. */
  @HostBinding('class.tu--clp')
  get esClp(): boolean {
    return this.par === 'CLP-USDT' || this.par === 'USDT-CLP';
  }

  /** Moneda local del par, para los textos («VES por USDT» / «CLP por USDT»). */
  get moneda(): 'VES' | 'CLP' {
    return this.esClp ? 'CLP' : 'VES';
  }

  /** «VES → USDT» / «USDT → VES» / «CLP → USDT» / «USDT → CLP», para los textos. */
  get etiquetaPar(): string {
    return this.esVenta ? `USDT → ${this.moneda}` : `${this.moneda} → USDT`;
  }

  // ── Llamadas según el par ──

  private pedirEditor(): Observable<EditorTramos> {
    if (this.par === 'CLP-USDT') return this.mainRates.obtenerEditorTramosClpUsdt();
    if (this.par === 'USDT-CLP') return this.mainRates.obtenerEditorTramosUsdtClp();
    return this.esVenta ? this.mainRates.obtenerEditorTramosUsdtVes() : this.mainRates.obtenerEditorTramosVesUsdt();
  }

  private pedirHistorial(pagina: number): Observable<HistorialTramosUsdtPagina> {
    if (this.par === 'CLP-USDT') return this.mainRates.obtenerHistorialTramosClpUsdt(pagina, T.HISTORIAL_TAMANO);
    if (this.par === 'USDT-CLP') return this.mainRates.obtenerHistorialTramosUsdtClp(pagina, T.HISTORIAL_TAMANO);
    return this.esVenta
      ? this.mainRates.obtenerHistorialTramosUsdtVes(pagina, T.HISTORIAL_TAMANO)
      : this.mainRates.obtenerHistorialTramosVesUsdt(pagina, T.HISTORIAL_TAMANO);
  }

  private pedirPrevisualizar(cuerpo: CuerpoTramosUsdt): Observable<VistaTramos> {
    if (this.par === 'CLP-USDT') return this.mainRates.previsualizarTramosClpUsdt(cuerpo);
    if (this.par === 'USDT-CLP') return this.mainRates.previsualizarTramosUsdtClp(cuerpo);
    return this.esVenta
      ? this.mainRates.previsualizarTramosUsdtVes(cuerpo)
      : this.mainRates.previsualizarTramosVesUsdt(cuerpo);
  }

  private pedirGuardar(cuerpo: CuerpoTramosUsdt, motivo: string): Observable<{ cambios: number }> {
    if (this.par === 'CLP-USDT') return this.mainRates.guardarTramosClpUsdt(cuerpo, motivo, this.version);
    if (this.par === 'USDT-CLP') return this.mainRates.guardarTramosUsdtClp(cuerpo, motivo, this.version);
    return this.esVenta
      ? this.mainRates.guardarTramosUsdtVes(cuerpo, motivo, this.version)
      : this.mainRates.guardarTramosVesUsdt(cuerpo, motivo, this.version);
  }

  /**
   * Cuerpo de previsualizar y guardar. VES: el de siempre. CLP: suma el tope
   * (sin él el back conserva el actual, pero acá siempre viaja).
   */
  private cuerpo(): CuerpoTramosUsdt | CuerpoTramosClp {
    const base = T.cuerpoDesdeBorrador(this.borrador as T.BorradorTramos);
    return this.esClp ? { ...base, topePct: Number(this.tope) } : base;
  }

  ngOnDestroy(): void {
    this.subVista?.unsubscribe();
    this.subHistorial?.unsubscribe();
  }

  /**
   * `reusar`: al abrir la pestaña, lo que ella misma leyó en este modal hace
   * menos de `REUSO_PESTANA_MS` (ver `leidos`) en vez de pedirlo otra vez.
   */
  cargar(conservarMensaje = false, reusar = false): void {
    this.cargando = true;
    this.errorCarga = null;
    const mensaje = this.mensaje;
    this.limpiarResultados();
    if (conservarMensaje) this.mensaje = mensaje;
    this.errorAccion = null;

    const previo = reusar ? this.leidos?.get(this.par) : undefined;
    if (previo && Date.now() - previo.en < REUSO_PESTANA_MS) {
      this.aplicarEditor(previo.r as EditorTramos);
      return;
    }

    const par = this.par;
    this.pedirEditor().subscribe({
      next: (r) => {
        this.leidos?.set(par, { en: Date.now(), r });
        this.aplicarEditor(r);
      },
      error: (err) => {
        this.editor = null;
        this.version = null;
        this.inicial = null;
        this.borrador = null;
        this.locales = [];
        this.segmentos = [];
        this.cambiosLista = [];
        const status = statusDe(err);
        this.errorCarga =
          status === 404 || status === 503
            ? `El editor de tramos ${this.etiquetaPar} no está disponible en este momento.`
            : erroresDe(err).join(' ');
        this.cargando = false;
      },
    });
  }

  /** Pinta lo leído del editor (recién pedido o reusado de este modal). */
  private aplicarEditor(r: EditorTramos): void {
    this.editor = r;
    this.version = versionDe(r);
    this.inicial = T.borradorDesde(r);
    this.borrador = T.clonar(this.inicial);
    this.topeInicial = this.esClp ? this.numeroONull((r as any)?.topePct) : null;
    this.tope = this.topeInicial;
    this.motivo = '';
    this.cargando = false;
    this.reiniciarHistorial(r);
    this.recalcular();
    this.cambios$.next({ inmediato: true });
  }

  // ───────────────────────── Lectura ─────────────────────────

  get filas(): T.FilaTramo[] {
    return this.borrador?.filas ?? [];
  }

  get referencia(): number | null {
    // Con el modelo de distancia la referencia es la del API (PROM P2P o spot).
    if (this.esDistancia) return this.numeroONull(this.estadoRuta?.referencia);
    return this.editor?.referencia ?? null;
  }

  get hayCambios(): boolean {
    return T.hayCambios(this.borrador, this.inicial) || this.topeCambio;
  }

  /** Solo CLP: el tope cambió frente a lo cargado. */
  private get topeCambio(): boolean {
    return this.esClp && !!this.borrador && Number(this.tope) !== Number(this.topeInicial);
  }

  private numeroONull(v: unknown): number | null {
    const n = Number(v);
    return v != null && v !== '' && Number.isFinite(n) ? n : null;
  }

  /** Comisión vigente de la base, solo lectura. */
  get comisionPct(): number | null {
    const c = Number((this.editor as any)?.comisionPct);
    return (this.editor as any)?.comisionPct != null && Number.isFinite(c) ? c : null;
  }

  /** Solo CLP: descuento base (`DESCUENTO_1` / `DESCUENTO_1_SELL`), en CLP por USDT. Solo lectura. */
  get descuento(): number | null {
    return this.esClp ? this.numeroONull((this.editor as any)?.descuento) : null;
  }

  /** Solo CLP→USDT: base de venta, el piso no baja de ella + 0,5 %. */
  get baseVenta(): number | null {
    return this.par === 'CLP-USDT' ? this.numeroONull((this.editor as EditorTramosClpUsdtResponse | null)?.baseVenta) : null;
  }

  /** Solo USDT→CLP: compra final más baja, el techo no pasa de ella ÷ 1,005. */
  get compraMinFinal(): number | null {
    return this.par === 'USDT-CLP'
      ? this.numeroONull((this.editor as EditorTramosUsdtClpResponse | null)?.compraMinFinal)
      : null;
  }

  /** Solo CLP: por qué hoy los tramos no rigen (null si rigen). */
  get textoSinTramos(): string | null {
    return this.esClp ? C.textoMotivoSinTramos((this.editor as any)?.motivoSinTramos, this.esVenta) : null;
  }

  /** Solo CLP→USDT: cuántos tramos se están cobrando ahora (el snapshot de la fila base vigente). */
  get publicados(): number | null {
    if (this.par !== 'CLP-USDT') return null;
    const p = (this.editor as EditorTramosClpUsdtResponse | null)?.publicados;
    return Array.isArray(p) ? p.length : null;
  }

  get errorTope(): string | null {
    return this.esClp ? C.errorTope(this.tope) : null;
  }

  /** Tasa de venta USDT→VES: el piso no baja de ella + 0,5 %. Solo VES→USDT. */
  get tasaVenta(): number | null {
    return this.esVenta || this.esClp ? null : (this.editor as EditorTramosUsdtResponse | null)?.tasaVentaUsdtVes ?? null;
  }

  /** Mejor tasa de compra VES→USDT: el techo no pasa de ella ÷ 1,005. Solo USDT→VES. */
  get mejorTasaCompra(): number | null {
    return this.esVenta && !this.esClp
      ? (this.editor as EditorTramosUsdtVesResponse | null)?.mejorTasaCompraVesUsdt ?? null
      : null;
  }

  /**
   * Solo CLP: margen mínimo EFECTIVO sobre/bajo el spot que, pasado al motor
   * de VES, da el piso/techo de la fórmula de CLP (igual que el back). En VES,
   * el margen mínimo del borrador tal cual.
   */
  private get margenMotor(): number | null {
    if (!this.esClp) return this.borrador?.margenMinPct ?? null;
    return this.esVenta
      ? C.margenEfectivoVenta(this.techo, this.referencia)
      : C.margenEfectivoCompra(this.piso, this.referencia);
  }

  /** Quien no edita tasas ve el editor sin campos ni guardado. */
  get soloLectura(): boolean {
    return !this.puedeEditar;
  }

  /** Banner de tasa manual vigente (`manualVigente` del back); null si no hay. */
  get textoManual(): string | null {
    return T.textoManualVigente((this.editor as any)?.manualVigente ?? null, this.esVenta);
  }

  /** Muchos tramos: la barra pasa a fichas que bajan de línea. */
  get muchosTramos(): boolean {
    return this.segmentos.length > T.MAX_TRAMOS_BARRA;
  }

  /** Número en es-CL con `dec` decimales. */
  n(x: number | null | undefined, dec = 2): string {
    return formatoNumero(x, dec);
  }

  /** Porcentaje «1,25 %»; `signo` antepone «+», `ceroGuion` muestra «—» en 0. */
  pct(x: number | null | undefined, signo = false, ceroGuion = false): string {
    return formatoPct(x, { signo, ceroGuion });
  }

  /** Rótulo del modo del tramo, para el modo solo lectura. */
  modoTexto(f: T.FilaTramo): string {
    return f.modo === 'PCT' ? 'Manual %' : 'Automático';
  }

  get manualFijo(): boolean {
    return (this.editor as any)?.modoBase === 'MANUAL_FIJO';
  }

  /** Aviso de tasa fija con la base y el piso del borrador. */
  get avisoManual(): string {
    if (!this.manualFijo) return '';
    return this.esVenta ? V.avisoManualFijo(this.base, this.techo) : T.avisoManualFijo(this.base, this.piso);
  }

  /** Cabe otro tramo activo (máximo 30; los apagados no cuentan). */
  get puedeAgregar(): boolean {
    return T.puedeAgregar(this.borrador);
  }

  get margenBase(): number | null {
    return this.esVenta ? V.margenBaseDe(this.referencia, this.base) : T.margenBaseDe(this.referencia, this.base);
  }

  get espacio(): number | null {
    return this.esVenta
      ? V.espacioDe(this.referencia, this.base, this.margenMotor)
      : T.espacioDe(this.referencia, this.base, this.margenMotor);
  }

  /** Solo compra (en la venta el candado es el techo). */
  get piso(): number | null {
    if (this.esVenta) return null;
    if (this.esClp) {
      return C.pisoClpUsdt({
        spot: this.referencia,
        base: this.base,
        margenMinPct: this.borrador?.margenMinPct,
        topePct: this.tope,
        baseVenta: this.baseVenta,
      });
    }
    return T.pisoDe(this.referencia, this.borrador?.margenMinPct, this.tasaVenta);
  }

  /** Solo venta: ningún tramo paga más que esto. */
  get techo(): number | null {
    if (!this.esVenta) return null;
    if (this.esClp) {
      return C.techoUsdtClp({
        spot: this.referencia,
        base: this.base,
        margenMinPct: this.borrador?.margenMinPct,
        topePct: this.tope,
        compraMinFinal: this.compraMinFinal,
      });
    }
    return V.techoDe(this.referencia, this.borrador?.margenMinPct, this.mejorTasaCompra);
  }

  /** El candado del par: piso (VES→USDT) o techo (USDT→VES). */
  get limite(): number | null {
    return this.esVenta ? this.techo : this.piso;
  }

  get motivoValido(): boolean {
    return T.motivoValido(this.motivo);
  }

  /**
   * Campos y estructura; con eso sano, que ningún tramo cobre más que el
   * anterior (el back también lo bloquea).
   */
  get erroresLocales(): string[] {
    if (!this.borrador) return [];
    const campos = T.erroresBorrador(this.borrador);
    // Modelo de distancia: el Manual % (mejora sobre la chica) va de 0 a 5.
    if (this.esDistancia) {
      this.filas.forEach((f) => {
        if (this.errorAjuste(f)) campos.push(`Tramo ${f.letra}: el Manual % va de 0 a ${D.AJUSTE_DISTANCIA_MAX} %.`);
      });
    }
    if (this.errorTope) campos.push(`El tope tiene que ser un número de 0 a ${C.TOPE_MAX_PCT} %.`);
    if (campos.length) return campos;
    const letras = this.filas.map((f) => f.letra);
    return this.esVenta ? V.erroresMonotonia(this.locales, letras) : T.erroresMonotonia(this.locales, letras);
  }

  get todosAuto(): boolean {
    return this.filas.every((f) => f.modo === 'AUTO');
  }

  get editable(): boolean {
    return this.puedeEditar && !this.guardando;
  }

  get puedeGuardar(): boolean {
    return (
      !!this.editor &&
      this.puedeEditar &&
      this.hayCambios &&
      this.motivoValido &&
      !this.erroresLocales.length &&
      !this.guardando &&
      !this.previsualizando &&
      !this.vistaPendiente
    );
  }

  get motivoNoGuardar(): string {
    if (!this.puedeEditar) return 'Tu usuario no tiene permisos para modificar las tasas.';
    if (!this.hayCambios) return 'No hay cambios que guardar.';
    if (this.erroresLocales.length) return 'Corrige los campos marcados en rojo.';
    if (!this.motivoValido) return 'Escribe el motivo del cambio (5 a 255 caracteres).';
    if (this.previsualizando || this.vistaPendiente) return 'Esperando la tasa oficial…';
    return '';
  }

  desde(i: number): number | null {
    return this.borrador ? T.desdeDe(this.borrador, i) : null;
  }

  errorDesde(i: number): string | null {
    return this.borrador ? T.errorDesdeFila(this.borrador, i) : null;
  }

  errorHasta(i: number): string | null {
    return this.borrador ? T.errorHastaFila(this.borrador, i) : null;
  }

  errorAjuste(f: T.FilaTramo): string | null {
    if (this.esDistancia && f.modo === 'PCT') {
      const a = Number(f.ajuste);
      return f.ajuste != null && Number.isFinite(a) && a >= 0 && a <= D.AJUSTE_DISTANCIA_MAX
        ? null
        : `De 0 a ${D.AJUSTE_DISTANCIA_MAX} %.`;
    }
    return T.errorAjuste(f);
  }

  get errorMargenMin(): string | null {
    return this.borrador ? T.errorMargenMin(this.borrador) : null;
  }

  get oficialAlDia(): boolean {
    return !!this.vista && !this.vistaVieja && !this.previsualizando;
  }

  /** Tasa oficial de la fila `i` (la vista previa trae solo activos, en orden). */
  oficial(i: number): TasaTramoUsdt | null {
    if (!this.oficialAlDia) return null;
    const t = (this.vista?.tasas || [])[i];
    return t && t.desde === this.desde(i) ? t : null;
  }

  /** Tasa, margen y recorte de la fila: oficial si está al día, si no la local. */
  fila(i: number): {
    tasa: number | null;
    margenPct: number | null;
    mejoraPct: number | null;
    recortado: boolean;
    oficial: boolean;
  } {
    const of = this.oficial(i);
    if (of) {
      return { tasa: of.tasa, margenPct: of.margenPct, mejoraPct: of.mejoraPct, recortado: !!of.recortado, oficial: true };
    }
    const l = this.locales[i];
    return l
      ? { tasa: l.tasa, margenPct: l.margenPct, mejoraPct: l.mejoraPct, recortado: l.recortado, oficial: false }
      : { tasa: null, margenPct: null, mejoraPct: null, recortado: false, oficial: false };
  }

  pagaPorEjemplo(i: number): number | null {
    const t = this.fila(i).tasa;
    return t == null ? null : t * T.USDT_EJEMPLO;
  }

  get simulacion(): T.SimulacionUsdt {
    const tramos = this.filas.map((_, i) => ({ desde: this.desde(i) }));
    // Modelo de distancia: el monto en moneda local pasa a USDT con `precioCorte` (si vino).
    const divisor = this.esDistancia ? D.divisorTramo(this.estadoRuta, this.base) : this.base;
    return this.esVenta ? V.simular(tramos, this.simMonto) : T.simular(tramos, divisor, this.simMonto);
  }

  /** Mensaje del simulador sin la palabra «VES» fija (CLP: el monto se escribe en CLP). */
  mensajeSimulacion(m: string | null): string | null {
    return this.esClp && m ? m.replace('VES', 'CLP') : m;
  }

  get filaSimulada(): number {
    return this.simulacion.indice;
  }

  get simTasa(): { tasa: number | null; oficial: boolean } | null {
    const i = this.filaSimulada;
    if (i < 0) return null;
    const f = this.fila(i);
    return { tasa: f.tasa, oficial: f.oficial };
  }

  /** VES→USDT: USDT que recibe. USDT→VES: VES que recibe. */
  get simRecibe(): number | null {
    return this.esVenta ? V.recibeVes(this.simMonto, this.simTasa?.tasa) : T.recibeUsdt(this.simMonto, this.simTasa?.tasa);
  }

  get simRecibeBase(): number | null {
    return this.esVenta ? V.recibeVes(this.simMonto, this.base) : T.recibeUsdt(this.simMonto, this.base);
  }

  get simMejora(): { usdt: number; pct: number } | null {
    const r = this.simRecibe;
    const b = this.simRecibeBase;
    if (r == null || b == null || !(b > 0)) return null;
    return { usdt: r - b, pct: (r / b - 1) * 100 };
  }

  etiquetaRango(i: number): string {
    return T.etiquetaRangoUsdt(this.desde(i), this.filas[i]?.hasta);
  }

  etiquetaClave(c: string): string {
    return T.etiquetaClave(c);
  }

  resumenCambio(h: HistorialTramoUsdt): string {
    return T.resumenHistorial(h);
  }

  trackFila(i: number, f: T.FilaTramo): string {
    return f.clave || `nuevo-${i}`;
  }

  trackIndice(i: number): number {
    return i;
  }

  // ───────────────────────── Edición ─────────────────────────

  marcarEditado(): void {
    if (this.vista) this.vistaVieja = true;
    this.vistaPendiente = true;
    this.mensaje = null;
    this.editado.emit();
    this.recalcular();
    this.cambios$.next({ inmediato: false });
  }

  /** La comisión se cambia en su modal de siempre: lo abre el modal padre. */
  pedirComision(): void {
    if (this.guardando) return;
    this.comisionPedida.emit();
  }

  cambiarMargenMin(valor: any): void {
    if (!this.borrador) return;
    this.borrador = { ...this.borrador, margenMinPct: valor === '' || valor == null ? null : Number(valor) };
    this.marcarEditado();
  }

  /** Solo CLP: tope % (0 a 5). */
  cambiarTope(valor: any): void {
    if (!this.borrador || !this.esClp) return;
    this.tope = valor === '' || valor == null ? null : Number(valor);
    this.marcarEditado();
  }

  cambiarDesde(i: number, valor: any): void {
    if (!this.borrador || i !== 0) return;
    this.borrador = T.cambiarDesdePrimera(this.borrador, valor);
    this.marcarEditado();
  }

  cambiarHasta(i: number, valor: any): void {
    if (!this.borrador) return;
    this.borrador = T.cambiarHasta(this.borrador, i, valor);
    this.marcarEditado();
  }

  cambiarModo(i: number, modo: ModoTramoUsdt): void {
    if (!this.borrador) return;
    // Modelo de distancia: Manual % es la mejora sobre la chica (no el margen sobre la referencia).
    const semilla = this.esDistancia ? this.fila(i).mejoraPct : this.fila(i).margenPct;
    this.borrador = T.cambiarModo(this.borrador, i, modo, semilla);
    this.marcarEditado();
  }

  cambiarAjuste(i: number, valor: any): void {
    if (!this.borrador) return;
    this.borrador = T.cambiarAjuste(this.borrador, i, valor);
    this.marcarEditado();
  }

  automaticos(): void {
    if (!this.borrador) return;
    this.borrador = T.todosAutomaticos(this.borrador);
    this.marcarEditado();
  }

  agregar(): void {
    if (!this.borrador) return;
    const r = T.agregarAlFinal(this.borrador);
    this.errorAccion = r.error;
    if (r.error) return;
    this.borrador = r.borrador;
    this.marcarEditado();
  }

  quitar(i: number): void {
    if (!this.borrador) return;
    const r = T.quitar(this.borrador, i, this.editor?.tramos);
    this.errorAccion = r.error;
    if (r.error) return;
    this.borrador = r.borrador;
    this.marcarEditado();
  }

  descartar(): void {
    if (this.inicial) this.borrador = T.clonar(this.inicial);
    this.tope = this.topeInicial;
    this.errorAccion = null;
    this.limpiarResultados();
    this.recalcular();
    this.cambios$.next({ inmediato: true });
  }

  /**
   * Base: siempre la del servidor (en manual fijo es la tasa fija). Los tramos
   * y el margen mínimo nunca la cambian.
   */
  private recalcular(): void {
    const b = this.borrador;
    if (!b || !this.editor) {
      this.base = null;
      this.locales = [];
      this.segmentos = [];
      this.cambiosLista = [];
      return;
    }
    this.base = Number(this.editor.base) > 0 ? Number(this.editor.base) : null;
    // CLP: el motor recibe el margen efectivo del piso/techo de CLP (como el back).
    const margen = this.margenMotor;
    this.locales = this.esDistancia
      ? D.calcularTasasDistancia(this.par, this.estadoRuta, b.filas, this.base)
      : this.esVenta
        ? V.calcularTasas(this.editor.referencia, this.base, margen, b.filas, this.mejorTasaCompra)
        : T.calcularTasas(this.editor.referencia, this.base, margen, b.filas, this.tasaVenta);
    this.segmentos = T.segmentosBarra(b);
    this.cambiosLista = T.resumenCambios(b, this.inicial);
    const tope = this.esClp ? C.cambioTope(this.topeInicial, this.tope) : null;
    if (tope) this.cambiosLista = [...this.cambiosLista, tope];
  }

  // ───────────────────────── Historial ─────────────────────────

  get paginasHistorial(): number {
    return totalPaginas(this.historialTotal, T.HISTORIAL_TAMANO);
  }

  get hayPaginacion(): boolean {
    return mostrarPaginacion(this.historialTotal, T.HISTORIAL_TAMANO);
  }

  toggleHistorial(): void {
    this.historialAbierto = !this.historialAbierto;
  }

  irAPagina(pagina: number): void {
    const destino = paginaValida(pagina, this.historialTotal, T.HISTORIAL_TAMANO);
    if (destino === this.historialPagina || this.cargandoHistorial) return;
    // Página 1 = la que ya vino con el editor (como en el de franjas).
    if (destino === 1 && this.editor) {
      this.reiniciarHistorial(this.editor);
      return;
    }
    this.cargandoHistorial = true;
    this.errorHistorial = null;
    this.subHistorial?.unsubscribe();
    this.subHistorial = this.pedirHistorial(destino)
      .subscribe({
        next: (r) => {
          this.cargandoHistorial = false;
          this.historialItems = r?.items || [];
          if (Number.isFinite(Number(r?.total))) this.historialTotal = Number(r.total);
          this.historialPagina = Number(r?.pagina) || destino;
        },
        error: (err) => {
          this.cargandoHistorial = false;
          this.errorHistorial = erroresDe(err).join(' ');
        },
      });
  }

  private reiniciarHistorial(r: EditorTramos | null): void {
    this.subHistorial?.unsubscribe();
    this.cargandoHistorial = false;
    this.errorHistorial = null;
    const h = r?.historial;
    const items = h?.items || [];
    this.historialItems = items.slice(0, T.HISTORIAL_TAMANO);
    this.historialPagina = 1;
    const total = Number(h?.total);
    this.historialTotal = Number.isFinite(total) && total >= 0 ? total : items.length;
  }

  // ───────────────────────── Servidor ─────────────────────────

  previsualizar(): void {
    this.cambios$.next({ inmediato: true });
  }

  private pedirVista(): Observable<ResultadoVista | null> {
    if (!this.editor || !this.borrador) return of(null);
    const locales = this.erroresLocales;
    if (locales.length) {
      this.previsualizando = false;
      return of({ ok: false, errores: locales } as ResultadoVista);
    }
    this.previsualizando = true;
    return this.pedirPrevisualizar(this.cuerpo()).pipe(
      map((r) => ({ ok: true, r } as ResultadoVista)),
      catchError((err) => of({ ok: false, errores: erroresDe(err) } as ResultadoVista)),
      tap(() => (this.previsualizando = false)),
    );
  }

  private aplicarVista(res: ResultadoVista | null): void {
    this.previsualizando = false;
    this.vistaPendiente = false;
    if (!res) return;
    if (res.ok) {
      this.vista = res.r;
      this.vistaVieja = false;
      this.errores = res.r?.errores || [];
      this.advertencias = res.r?.advertencias || [];
    } else {
      this.vista = null;
      this.vistaVieja = false;
      this.errores = res.errores;
      this.advertencias = [];
    }
  }

  async guardar(): Promise<void> {
    if (!this.puedeGuardar || !this.editor || !this.borrador) return;

    const confirmacion = await Swal.fire({
      title: `¿Guardar los tramos ${this.etiquetaPar}?`,
      html: this.par === 'CLP-USDT'
        ? '<p style="color:#92400e">Las tasas nuevas rigen desde la próxima tasa base (unos 2 a 4 minutos). La tasa base no cambia.</p>'
        : '<p style="color:#92400e">Las tasas nuevas se aplican al cliente al guardar. La tasa base no cambia.</p>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (!confirmacion.isConfirmed) return;

    this.guardando = true;
    this.errores = [];
    this.advertencias = [];
    this.mensaje = null;

    this.pedirGuardar(this.cuerpo(), this.motivo.trim())
      .subscribe({
        next: (r) => {
          this.guardando = false;
          const cambios = Number(r?.cambios) || 0;
          this.guardado.emit({ par: this.par, cambios, aplicado: true });
          this.mensaje =
            cambios <= 0
              ? 'No había cambios que guardar.'
              : this.par === 'CLP-USDT'
                ? 'Tramos guardados: rigen desde la próxima tasa base.'
                : 'Tramos guardados y aplicados.';
          this.aviso('success', this.mensaje);
          this.cargar(true);
        },
        error: (err) => {
          this.guardando = false;
          // Prod: 403 si el correo no está autorizado. Se muestra su mensaje.
          const sinPermiso = mensajeSinPermiso(err);
          if (sinPermiso) {
            this.errores = [sinPermiso];
            Swal.fire({
              icon: 'warning',
              title: 'Acción no disponible',
              text: sinPermiso,
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#f59e0b',
            });
            return;
          }
          // 409: otro operador guardó antes. Su mensaje y la opción de recargar.
          const conflicto = mensajeConflicto(err, 'tramos');
          if (conflicto) {
            this.errores = [conflicto];
            this.avisoConflicto(conflicto);
            return;
          }
          this.errores = erroresDe(err);
          this.aviso('error', 'No se pudieron guardar los tramos. Revisa los errores.');
        },
      });
  }

  /**
   * 409 del back: otro operador guardó mientras se editaba. Mismo aviso que
   * el de sin permiso, con la opción de recargar (se pierde el borrador).
   */
  private async avisoConflicto(texto: string): Promise<void> {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: texto,
      showCancelButton: true,
      confirmButtonText: 'Recargar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (r.isConfirmed) this.cargar();
  }

  private aviso(icon: 'success' | 'error', title: string): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      title,
      showConfirmButton: false,
      timer: icon === 'success' ? 3500 : 5000,
      timerProgressBar: true,
    });
  }

  private limpiarResultados(): void {
    this.vista = null;
    this.vistaVieja = false;
    this.errores = [];
    this.advertencias = [];
    this.mensaje = null;
  }
}
